'use client';

import { useCallback, useState } from 'react';

import SliderNavigation from '@/components/ui/SliderNavigation';
import RichText from '@/components/ui/RichText/RichText';
import { useCarousel } from '@/hooks/useCarousel';

import CarouselItem from '../CarouselItem/CarouselItem';

const CarouselContainer = ({
  data,
  items = [],
  activeIndex,
  setActiveIndex,
  onNext,
  onPrev,
}: any) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { scrollerRef, itemsRef, isMobile, touchHandlers } = useCarousel(
    activeIndex,
    items.length,
    setActiveIndex,
  );

  // This only changes the concern - no scroll
  const handleSelect = useCallback(
    (idx: number) => {
      setActiveIndex(idx);
    },
    [setActiveIndex],
  );

  // This changes concern AND scrolls
  const handleButtonSelect = useCallback(
    (idx: number) => {
      setActiveIndex(idx);
      // Small delay to let the concern change first
      setTimeout(() => {
        document
          .getElementById('treatments')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    },
    [setActiveIndex],
  );

  if (!data || !items.length) return null;

  return (
    <div className="flex flex-col gap-8">
      <div className="relative right-1/2 left-1/2 mr-[-50vw] ml-[-50vw] w-screen lg:ml-[-51.5vw] lg:pl-[calc((100vw-var(--width-xs))/2)]">
        <div className="grid grid-cols-1 items-center gap-8 overflow-x-hidden lg:grid-cols-4 lg:items-stretch">
          {/* Static Info Box */}
          <div className="bg-theme-brand-blue mr-24 flex flex-col justify-center rounded-r-2xl p-8 text-white shadow-lg lg:mr-4 lg:max-h-84 lg:w-full lg:self-center lg:pr-6 lg:pl-12">
            <RichText styles="text-heading-md font-bold ">
              {data.heading}
            </RichText>
            <RichText styles="text-body text-white lg:text-h5 opacity-90 mt-4">
              {data.description}
            </RichText>
          </div>

          {/* Carousel Scroller */}
          <div className="col-span-1 lg:col-span-3">
            <div
              ref={scrollerRef}
              {...touchHandlers}
              className="hide-scrollbar overflow-x-hidden scroll-smooth"
              style={{ touchAction: 'pan-y' }}
            >
              <div className="flex w-max gap-4 p-4">
                {items.map((item: any, idx: number) => (
                  <CarouselItem
                    key={item?.id || idx}
                    item={item}
                    idx={idx}
                    hoveredIndex={hoveredIndex}
                    isActive={idx === activeIndex}
                    isDimmed={hoveredIndex !== null && hoveredIndex !== idx}
                    isMobile={isMobile}
                    innerRef={(el) => {
                      itemsRef.current[idx] = el;
                    }}
                    onSelect={() => handleSelect(idx)}
                    onButtonSelect={() => handleButtonSelect(idx)}
                    onMouseEnter={() => setHoveredIndex(idx)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dot + arrow navigation for all viewports */}
      <SliderNavigation
        onPrev={onPrev}
        onNext={onNext}
        activeIndex={activeIndex}
        totalItems={items.length}
        onDotClick={handleButtonSelect}
      />
    </div>
  );
};

export default CarouselContainer;
